import { useState } from 'react';
import {
  MoreHorizontal,
  Trash2,
  Pencil,
  Plus,
  Camera,
  FileText,
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  MenuRoot,
  MenuTrigger,
  MenuPortal,
  MenuPositioner,
  MenuPopup,
  MenuItem,
  MenuSeparator,
} from '../ui/menu';
import {
  DialogRoot,
  DialogPortal,
  DialogBackdrop,
  DialogPopup,
  DialogHeader,
  DialogTitle,
  DialogContent,
  DialogCloseButton,
} from '../ui/dialog';
import { SnapshotForm } from './snapshot-form';
import { LogForm } from './log-form';
import { formatCurrency } from '../../lib/format';
import { useDate } from '../../contexts/date-context';
import type { Caixinha, CaixinhaSnapshot, CaixinhaLog } from '../../types';

function formatDate(date: Date): string {
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  });
}

function computeAllPeriodYields(
  snapshots: CaixinhaSnapshot[],
  logs: CaixinhaLog[]
): Array<{ label: string; amount: number; pct: number }> {
  const sorted = [...snapshots].sort(
    (a, b) => a.date.getTime() - b.date.getTime()
  );

  // Build anchor list: prepend a virtual origin (value=0) if there are logs
  // before the first real snapshot and no snapshot precedes them.
  const anchors: CaixinhaSnapshot[] = [...sorted];
  if (sorted.length >= 1) {
    const firstSnap = sorted[0]!;
    const logsBeforeFirst = [...logs]
      .filter((l) => l.date.getTime() < firstSnap.date.getTime())
      .sort((a, b) => a.date.getTime() - b.date.getTime());
    if (logsBeforeFirst.length > 0) {
      anchors.unshift({
        id: '__virtual__',
        date: logsBeforeFirst[0]!.date,
        value: 0,
      });
    }
  }

  if (anchors.length < 2) return [];

  return anchors.slice(1).map((snap, i) => {
    const prev = anchors[i]!;
    const logsSum = logs
      .filter(
        (l) =>
          l.date.getTime() >= prev.date.getTime() &&
          l.date.getTime() <= snap.date.getTime()
      )
      .reduce((s, l) => s + l.value, 0);
    const base = prev.value + logsSum;
    const amount = snap.value - base;
    const pct = base !== 0 ? (amount / base) * 100 : 0;
    return {
      label: `${formatDate(prev.date)} → ${formatDate(snap.date)}`,
      amount,
      pct,
    };
  });
}

function computeTotalYield(
  snapshots: CaixinhaSnapshot[],
  logs: CaixinhaLog[]
): { amount: number; pct: number } | null {
  const sorted = [...snapshots].sort(
    (a, b) => a.date.getTime() - b.date.getTime()
  );
  if (sorted.length < 1) return null;
  const last = sorted[sorted.length - 1]!;
  const first = sorted[0]!;
  // Use virtual origin (value=0) if there are logs before the first snapshot
  const hasLogsBeforeFirst = logs.some(
    (l) => l.date.getTime() < first.date.getTime()
  );
  const startValue = hasLogsBeforeFirst ? 0 : first.value;
  const totalLogs = logs
    .filter((e) => e.date.getTime() < last.date.getTime())
    .reduce((s, l) => s + l.value, 0);
  const base = startValue + totalLogs;
  const amount = last.value - base;
  const pct = base !== 0 ? (amount / base) * 100 : 0;
  return { amount, pct };
}

interface CaixinhaCardProps {
  caixinha: Caixinha;
  onEdit: (caixinha: Caixinha) => void;
  onDelete: (caixinha: Caixinha) => void;
  onAddSnapshot: (
    caixinhaId: string,
    data: { date: Date; value: number }
  ) => Promise<void>;
  onUpdateSnapshot: (
    caixinhaId: string,
    snapshotId: string,
    data: Partial<Pick<CaixinhaSnapshot, 'date' | 'value'>>
  ) => Promise<void>;
  onDeleteSnapshot: (caixinhaId: string, snapshotId: string) => Promise<void>;
  onAddLog: (
    caixinhaId: string,
    data: { date: Date; value: number; description: string }
  ) => Promise<void>;
  onUpdateLog: (
    caixinhaId: string,
    logId: string,
    data: Partial<Pick<CaixinhaLog, 'date' | 'value' | 'description'>>
  ) => Promise<void>;
  onDeleteLog: (caixinhaId: string, logId: string) => Promise<void>;
}

export function CaixinhaCard({
  caixinha,
  onEdit,
  onDelete,
  onAddSnapshot,
  onUpdateSnapshot,
  onDeleteSnapshot,
  onAddLog,
  onUpdateLog,
  onDeleteLog,
}: CaixinhaCardProps) {
  const { month, year } = useDate();
  const [snapshotDialogOpen, setSnapshotDialogOpen] = useState(false);
  const [editingSnapshot, setEditingSnapshot] =
    useState<CaixinhaSnapshot | null>(null);
  const [logDialogOpen, setLogDialogOpen] = useState(false);
  const [editingLog, setEditingLog] = useState<CaixinhaLog | null>(null);
  const [logsExpanded, setLogsExpanded] = useState(false);

  // End of the selected month (last millisecond)
  const endOfMonth = new Date(year, month, 0, 23, 59, 59, 999);

  const filteredSnapshots = caixinha.snapshots.filter(
    (s) => s.date.getTime() <= endOfMonth.getTime()
  );
  const filteredLogs = caixinha.log.filter(
    (l) => l.date.getTime() <= endOfMonth.getTime()
  );

  const sortedSnapshots = [...filteredSnapshots].sort(
    (a, b) => b.date.getTime() - a.date.getTime()
  );
  const sortedLogs = [...filteredLogs].sort((a, b) =>
    b.date.getTime() <= a.date.getTime() ? -1 : 1
  );
  const lastSnapshot = sortedSnapshots[0] ?? null;
  const currentValue = lastSnapshot
    ? lastSnapshot.value +
      filteredLogs
        .filter((l) => l.date.getTime() > lastSnapshot.date.getTime())
        .reduce((s, l) => s + l.value, 0)
    : filteredLogs.length > 0
      ? filteredLogs.reduce((s, l) => s + l.value, 0)
      : null;
  const periodYields = computeAllPeriodYields(filteredSnapshots, filteredLogs);
  const totalYield = computeTotalYield(filteredSnapshots, filteredLogs);

  const now = new Date();
  const isCurrentMonth =
    year === now.getFullYear() && month === now.getMonth() + 1;
  if (
    filteredSnapshots.length === 0 &&
    filteredLogs.length === 0 &&
    !isCurrentMonth
  )
    return null;

  const handleSnapshotSubmit = async (data: { date: Date; value: number }) => {
    if (editingSnapshot) {
      await onUpdateSnapshot(caixinha.id, editingSnapshot.id, data);
    } else {
      await onAddSnapshot(caixinha.id, data);
    }
    setSnapshotDialogOpen(false);
    setEditingSnapshot(null);
  };

  const handleLogSubmit = async (data: {
    date: Date;
    value: number;
    description: string;
  }) => {
    if (editingLog) {
      await onUpdateLog(caixinha.id, editingLog.id, data);
    } else {
      await onAddLog(caixinha.id, data);
    }
    setLogDialogOpen(false);
    setEditingLog(null);
  };

  return (
    <div className="flex flex-col gap-4 rounded-xl border border-border bg-white p-4 shadow-sm">
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="truncate text-sm font-semibold text-foreground">
            {caixinha.title}
          </h3>
          {caixinha.description && (
            <p className="mt-0.5 text-xs text-muted-foreground line-clamp-2">
              {caixinha.description}
            </p>
          )}
        </div>
        <MenuRoot>
          <MenuTrigger
            render={
              <Button
                variant="ghost"
                size="sm"
                className="shrink-0 p-1 text-muted-foreground"
              >
                <MoreHorizontal className="size-4" />
              </Button>
            }
          />
          <MenuPortal>
            <MenuPositioner sideOffset={4}>
              <MenuPopup>
                <MenuItem onClick={() => onEdit(caixinha)}>
                  <Pencil className="size-4" />
                  Editar caixinha
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setEditingSnapshot(null);
                    setSnapshotDialogOpen(true);
                  }}
                >
                  <Camera className="size-4" />
                  Novo snapshot
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setEditingLog(null);
                    setLogDialogOpen(true);
                  }}
                >
                  <Plus className="size-4" />
                  Nova movimentação
                </MenuItem>
                <MenuSeparator />
                <MenuItem
                  onClick={() => onDelete(caixinha)}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="size-4" />
                  Excluir caixinha
                </MenuItem>
              </MenuPopup>
            </MenuPositioner>
          </MenuPortal>
        </MenuRoot>
      </div>

      {/* Current value */}
      {currentValue !== null ? (
        <div>
          <span className="text-xs text-muted-foreground">Valor atual</span>
          <p className="text-xl font-bold text-foreground">
            R$ {formatCurrency(currentValue)}
          </p>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground italic">
          Sem snapshots registrados
        </p>
      )}

      {/* Yields */}
      {(periodYields.length > 0 || totalYield) && (
        <div className="flex flex-col gap-1 border-t border-border pt-3">
          {periodYields.map((p, i) => (
            <div
              key={i}
              className="flex items-center justify-between gap-2 rounded-md px-2 py-1"
            >
              <span className="text-xs text-muted-foreground">{p.label}</span>
              <div className="flex items-center gap-2">
                <span
                  className={`text-xs font-medium ${p.amount >= 0 ? 'text-success' : 'text-destructive'}`}
                >
                  {p.amount >= 0 ? '+' : ''}R$ {formatCurrency(p.amount)}
                </span>
                <span
                  className={`w-14 text-right text-xs ${p.amount >= 0 ? 'text-success' : 'text-destructive'}`}
                >
                  {p.amount >= 0 ? '+' : ''}
                  {p.pct.toFixed(2)}%
                </span>
              </div>
            </div>
          ))}
          {totalYield && (
            <div className="flex items-center justify-between gap-2 rounded-md border-t border-border px-2 pt-2 mt-1">
              <span className="text-xs font-semibold text-foreground">
                Total
              </span>
              <div className="flex items-center gap-2">
                <span
                  className={`text-xs font-semibold ${totalYield.amount >= 0 ? 'text-success' : 'text-destructive'}`}
                >
                  {totalYield.amount >= 0 ? '+' : ''}R${' '}
                  {formatCurrency(totalYield.amount)}
                </span>
                <span
                  className={`w-14 text-right text-xs font-semibold ${totalYield.amount >= 0 ? 'text-success' : 'text-destructive'}`}
                >
                  {totalYield.amount >= 0 ? '+' : ''}
                  {totalYield.pct.toFixed(2)}%
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Snapshots */}
      <div className="border-t border-border pt-3">
        <div className="mb-2 flex items-center justify-between">
          <span className="flex items-center gap-1 text-xs font-medium text-muted-foreground">
            <Camera className="size-3" />
            Snapshots ({sortedSnapshots.length})
          </span>
          <button
            type="button"
            onClick={() => {
              setEditingSnapshot(null);
              setSnapshotDialogOpen(true);
            }}
            className="flex items-center gap-1 text-xs text-primary hover:underline"
          >
            <Plus className="size-3" /> Adicionar
          </button>
        </div>
        {sortedSnapshots.length === 0 ? (
          <p className="text-xs text-muted-foreground">Nenhum snapshot</p>
        ) : (
          <ul className="flex flex-col gap-1">
            {sortedSnapshots.map((s) => (
              <li
                key={s.id}
                className="flex items-center justify-between gap-2 rounded-md bg-surface px-2 py-1.5"
              >
                <span className="text-xs text-muted-foreground">
                  {formatDate(s.date)}
                </span>
                <span className="flex-1 text-right text-xs font-medium text-foreground">
                  R$ {formatCurrency(s.value)}
                </span>
                <MenuRoot>
                  <MenuTrigger
                    render={
                      <button
                        type="button"
                        className="text-muted-foreground hover:text-foreground"
                      >
                        <MoreHorizontal className="size-3.5" />
                      </button>
                    }
                  />
                  <MenuPortal>
                    <MenuPositioner sideOffset={4}>
                      <MenuPopup>
                        <MenuItem
                          onClick={() => {
                            setEditingSnapshot(s);
                            setSnapshotDialogOpen(true);
                          }}
                        >
                          <Pencil className="size-4" /> Editar
                        </MenuItem>
                        <MenuSeparator />
                        <MenuItem
                          onClick={() => onDeleteSnapshot(caixinha.id, s.id)}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="size-4" /> Excluir
                        </MenuItem>
                      </MenuPopup>
                    </MenuPositioner>
                  </MenuPortal>
                </MenuRoot>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Logs */}
      <div className="border-t border-border pt-3">
        <div className="mb-2 flex items-center justify-between">
          <span className="flex items-center gap-1 text-xs font-medium text-muted-foreground">
            <FileText className="size-3" />
            Movimentações ({sortedLogs.length})
          </span>
          <button
            type="button"
            onClick={() => {
              setEditingLog(null);
              setLogDialogOpen(true);
            }}
            className="flex items-center gap-1 text-xs text-primary hover:underline"
          >
            <Plus className="size-3" /> Adicionar
          </button>
        </div>
        {sortedLogs.length === 0 ? (
          <p className="text-xs text-muted-foreground">Nenhuma movimentação</p>
        ) : (
          <>
            <ul className="flex flex-col gap-1">
              {(logsExpanded ? sortedLogs : sortedLogs.slice(0, 3)).map((l) => (
                <li
                  key={l.id}
                  className="flex items-center justify-between gap-2 rounded-md bg-surface px-2 py-1.5"
                >
                  <span className="text-xs text-muted-foreground shrink-0">
                    {formatDate(l.date)}
                  </span>
                  <span className="flex-1 truncate text-xs text-muted-foreground">
                    {l.description || '—'}
                  </span>
                  <span
                    className={`shrink-0 text-xs font-medium ${l.value >= 0 ? 'text-success' : 'text-destructive'}`}
                  >
                    {l.value >= 0 ? '+' : ''}R${' '}
                    {formatCurrency(Math.abs(l.value))}
                  </span>
                  <MenuRoot>
                    <MenuTrigger
                      render={
                        <button
                          type="button"
                          className="text-muted-foreground hover:text-foreground"
                        >
                          <MoreHorizontal className="size-3.5" />
                        </button>
                      }
                    />
                    <MenuPortal>
                      <MenuPositioner sideOffset={4}>
                        <MenuPopup>
                          <MenuItem
                            onClick={() => {
                              setEditingLog(l);
                              setLogDialogOpen(true);
                            }}
                          >
                            <Pencil className="size-4" /> Editar
                          </MenuItem>
                          <MenuSeparator />
                          <MenuItem
                            onClick={() => onDeleteLog(caixinha.id, l.id)}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="size-4" /> Excluir
                          </MenuItem>
                        </MenuPopup>
                      </MenuPositioner>
                    </MenuPortal>
                  </MenuRoot>
                </li>
              ))}
            </ul>
            {sortedLogs.length > 3 && (
              <button
                type="button"
                onClick={() => setLogsExpanded((v) => !v)}
                className="mt-1 w-full text-xs text-primary hover:underline text-center"
              >
                {logsExpanded ? 'Esconder' : `Ver tudo (${sortedLogs.length})`}
              </button>
            )}
          </>
        )}
      </div>

      {/* Snapshot dialog */}
      <DialogRoot
        open={snapshotDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setSnapshotDialogOpen(false);
            setEditingSnapshot(null);
          }
        }}
      >
        <DialogPortal>
          <DialogBackdrop />
          <DialogPopup className="max-w-sm">
            <DialogHeader>
              <DialogTitle>
                {editingSnapshot ? 'Editar snapshot' : 'Novo snapshot'}
              </DialogTitle>
              <DialogCloseButton />
            </DialogHeader>
            <DialogContent>
              <SnapshotForm
                onSubmit={handleSnapshotSubmit}
                onClose={() => {
                  setSnapshotDialogOpen(false);
                  setEditingSnapshot(null);
                }}
                initialData={editingSnapshot}
              />
            </DialogContent>
          </DialogPopup>
        </DialogPortal>
      </DialogRoot>

      {/* Log dialog */}
      <DialogRoot
        open={logDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setLogDialogOpen(false);
            setEditingLog(null);
          }
        }}
      >
        <DialogPortal>
          <DialogBackdrop />
          <DialogPopup className="max-w-sm">
            <DialogHeader>
              <DialogTitle>
                {editingLog ? 'Editar movimentação' : 'Nova movimentação'}
              </DialogTitle>
              <DialogCloseButton />
            </DialogHeader>
            <DialogContent>
              <LogForm
                onSubmit={handleLogSubmit}
                onClose={() => {
                  setLogDialogOpen(false);
                  setEditingLog(null);
                }}
                initialData={editingLog}
              />
            </DialogContent>
          </DialogPopup>
        </DialogPortal>
      </DialogRoot>
    </div>
  );
}
